import React, {
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useState,
} from 'react'

import { AuthContext } from '@deltek/specification-client/core/Configurations/AuthContext'
import GridContainer from '@deltek/specification-client/core/Surfaces/GridContainer'
import { Dialog, Typography } from '@material-ui/core'
import Button from '@material-ui/core/Button'
import DialogActions from '@material-ui/core/DialogActions'
import DialogContent from '@material-ui/core/DialogContent'
import IconButton from '@material-ui/core/IconButton'
import CloseIcon from '@material-ui/icons/Close'
import { getSelectedValuesFromQueryString } from 'bpm/pages/ProductsPage/productsUtils'
import { useLocation } from 'react-router-dom'
import { AddNotesModal } from './AddNotesModal'
import EmptyNotes from './assets/EmptyNotes'
import NotesArrow from './assets/NotesArrow'
import NotesFilters from './filters/NotesFilters'
import usePDNFiltersHook from './hooks/usePDNFiltersHook'
import usePDNhook from './hooks/usePDNHook'
import {
    BUTTON_ADD_NEW,
    LABEL_CREATE_NOTES_1,
    LABEL_CREATE_NOTES_2,
    LABEL_NOTES,
    VARIABLE_NOTES_COUNT,
    noteTypes,
} from './ListingNotes.constants'
import {
    NoteType,
    NoteTypeReasonOption,
    ProductDetailNoteData,
} from './ListingNotes.types'
import { ListingNotesCard } from './ListingNotesCard'
import useStyles from './ListingNotesModal.styles'

export type ListingNotesModalStyleProps = {
    headerColor?: string
    isViewOnly: boolean
}

export interface ListingNotesModalProps {
    open: boolean
    onClose: () => void
    isOnEditMode: boolean
    isManufacturerListing: boolean
    hideFilters?: boolean
    selectedNotes?: string[]
    filterFromQuery?: boolean
}

const ListingNotesModal: React.FC<ListingNotesModalProps> = ({
    open,
    onClose,
    isOnEditMode,
    isManufacturerListing,
    hideFilters,
    selectedNotes,
    filterFromQuery,
}) => {
    const {
        editableNote,
        handleEditNote,
        handleCancelEditNote,
        handleDeleteNote,
        handleSaveNote,
        pdnData,
    } = usePDNhook()

    const location = useLocation()

    const { currentUserInfo: currentUser } = useContext(AuthContext)
    const {
        clearFilters,
        filters: notesFilter,
        setNoteTypes,
    } = usePDNFiltersHook()

    const isDeltekPublishingAuthor = useMemo(
        () => currentUser.roles?.includes('PublishingAuthor'),
        [currentUser]
    )

    const isViewOnly = useMemo(() => {
        return !(
            isOnEditMode ||
            (isDeltekPublishingAuthor && isManufacturerListing)
        )
    }, [isDeltekPublishingAuthor, isOnEditMode, isManufacturerListing])

    const classes = useStyles({
        isViewOnly,
    })

    const [addNotesModalOpen, setAddNotesModalOpen] = useState(false)

    const filteredNotes = useMemo(() => {
        let filteredData = [...pdnData]

        if (notesFilter.users?.length) {
            filteredData = filteredData.filter(
                (note: Partial<ProductDetailNoteData>) =>
                    notesFilter.users.includes(note.userFullName ?? '')
            )
        }

        if (notesFilter.noteTypes?.length) {
            filteredData = filteredData.filter(
                (note: Partial<ProductDetailNoteData>) =>
                    !!notesFilter.noteTypes.find(
                        ({ reason }: { reason: string }) =>
                            reason === note.reason
                    )
            )
        }

        if (!!notesFilter.dateFrom || !!notesFilter.dateTo) {
            const dateFrom = !!notesFilter.dateFrom
                ? new Date(notesFilter.dateFrom).getTime()
                : new Date('Jan 1, 1970').getTime()

            const dateTo = !!notesFilter.dateTo
                ? new Date(`${notesFilter.dateTo} 23:59:59`).getTime()
                : new Date().getTime()

            filteredData = filteredData.filter((note) => {
                const checkTime = new Date(note.createdDate ?? '').getTime()
                return checkTime < dateTo && checkTime >= dateFrom
            })
        }

        if (!!selectedNotes && selectedNotes?.length) {
            filteredData = [
                ...selectedNotes
                    .map(
                        (noteId) =>
                            pdnData.find((note) => note.id === noteId) ?? {}
                    )
                    .filter((note) => {
                        if (Object.keys(note).length !== 0) {
                            return true
                        }

                        return false
                    }),
            ]
        }

        return filteredData.sort(
            (a, b) =>
                new Date(b.createdDate ?? '').getTime() -
                new Date(a.createdDate ?? '').getTime()
        )
    }, [pdnData, notesFilter, selectedNotes])

    const pdnNoteTypes = useMemo(() => {
        return [
            ...new Set(
                pdnData
                    .sort(
                        (
                            a: Partial<ProductDetailNoteData>,
                            b: Partial<ProductDetailNoteData>
                        ) => {
                            if (a.noteType! > b.noteType!) {
                                return 1
                            }
                            if (a.noteType! < b.noteType!) {
                                return -1
                            }
                            return 0
                        }
                    )
                    .map(
                        ({
                            noteType,
                            reason,
                        }: Partial<ProductDetailNoteData>) =>
                            JSON.stringify({
                                noteType:
                                    noteTypes[
                                        noteType as keyof typeof noteTypes
                                    ],
                                reason,
                            })
                    )
            ),
        ].map((stringifiedOptions) => JSON.parse(stringifiedOptions))
    }, [pdnData, noteTypes])

    const hasFilters = useMemo(
        () =>
            !!notesFilter.users.length ||
            !!notesFilter.noteTypes.length ||
            !!notesFilter.dateFrom ||
            !!notesFilter.dateTo,
        [notesFilter]
    )

    const noteToEdit = useMemo(() => {
        if (editableNote) {
            return pdnData?.[
                pdnData?.findIndex(
                    (note: Partial<ProductDetailNoteData>) =>
                        note?.id === editableNote
                )
            ]
        }
        return undefined
    }, [pdnData, editableNote])

    const handleOpenAddNotes = useCallback(() => setAddNotesModalOpen(true), [])

    const handleCancel = useCallback(() => {
        setAddNotesModalOpen(false)
        handleCancelEditNote?.()
        clearFilters?.()
    }, [handleCancelEditNote, clearFilters])

    useEffect(() => {
        if (!!editableNote && !addNotesModalOpen) {
            setAddNotesModalOpen(true)
        }
    }, [addNotesModalOpen, editableNote])

    useEffect(() => {
        if (filterFromQuery && !!open) {
            const values = getSelectedValuesFromQueryString(location.search)
            const reason = !!values?.noteReasons?.length
                ? {
                      noteType: values.noteReasons[0].split('-')[0] as NoteType,
                      reason: values.noteReasons[0].split('-')[1],
                  }
                : {}

            if (!!Object.keys(reason).length) {
                setNoteTypes?.([reason as NoteTypeReasonOption])
            }
        }
    }, [location, setNoteTypes, open])

    return (
        <>
            <Dialog
                open={open}
                fullWidth
                maxWidth="lg"
                data-testid="listingNotesModal"
                className={classes.dialog}
                aria-labelledby="listing-notes"
            >
                <GridContainer className={classes.headerContainer}>
                    <IconButton
                        onClick={onClose}
                        className={classes.closeIconContainer}
                    >
                        <CloseIcon className={classes.closeIcon} />
                    </IconButton>
                    <Typography
                        component="span"
                        className={classes.notesHeaderContainer}
                    >
                        <Typography
                            component="span"
                            className={classes.notesHeader}
                        >
                            {LABEL_NOTES}{' '}
                        </Typography>

                        {VARIABLE_NOTES_COUNT(filteredNotes?.length ?? 0)}
                    </Typography>
                </GridContainer>

                <DialogContent className={classes.dialogContent}>
                    <>
                        {!hideFilters && (
                            <NotesFilters noteTypes={pdnNoteTypes ?? []} />
                        )}
                        {!!filteredNotes?.length ? (
                            filteredNotes?.map(
                                (
                                    item: Partial<ProductDetailNoteData>,
                                    index: number
                                ) => (
                                    <ListingNotesCard
                                        mode={!isViewOnly ? 'edit' : 'display'}
                                        key={`${index}-${item.id}`}
                                        note={item}
                                        onEdit={handleEditNote}
                                        onDelete={handleDeleteNote}
                                        currentUserId={currentUser?.kpUserId}
                                        enableEdit={isDeltekPublishingAuthor}
                                    />
                                )
                            )
                        ) : !!hasFilters ? (
                            <Typography
                                component="div"
                                align="center"
                                className={classes.noResults}
                            >
                                <h3> No Results </h3>
                            </Typography>
                        ) : (
                            <Typography
                                component="span"
                                className={classes.emptyNotesContainer}
                            >
                                <EmptyNotes />
                                <Typography
                                    component="span"
                                    className={classes.emptyNotesDescription}
                                >
                                    {LABEL_CREATE_NOTES_1}
                                </Typography>
                                {!isViewOnly && (
                                    <>
                                        <Typography
                                            component="span"
                                            className={
                                                classes.emptyNotesDescription
                                            }
                                        >
                                            {LABEL_CREATE_NOTES_2}
                                        </Typography>
                                        <Typography
                                            className={classes.emptyNotesArrow}
                                            component="span"
                                        >
                                            <NotesArrow />
                                        </Typography>
                                    </>
                                )}
                            </Typography>
                        )}
                    </>
                </DialogContent>
                {!isViewOnly && (
                    <>
                        <Typography
                            component="div"
                            className={classes.horizontalRule}
                        />
                        <DialogActions className={classes.dialogAction}>
                            <Button
                                data-testid="btnAddNew"
                                className={classes.actionButton}
                                onClick={handleOpenAddNotes}
                            >
                                {BUTTON_ADD_NEW}
                            </Button>
                        </DialogActions>
                    </>
                )}
            </Dialog>
            {!!addNotesModalOpen && (
                <AddNotesModal
                    open={addNotesModalOpen}
                    onSave={handleSaveNote}
                    onCancel={handleCancel}
                    isEditMode={!isViewOnly}
                    noteType={noteToEdit?.noteType}
                    note={noteToEdit}
                    action={noteToEdit?.noteType}
                />
            )}
        </>
    )
}

export default ListingNotesModal
